import { useEffect, useState, useMemo } from "react";
import { Form } from "antd";
import RecordSelect from "./RecordSelect";
import { Identifiable, RecordFieldSchema, RecordModel } from "../../types/core";

interface RelatedFieldSelectProps<T> {
  fieldName: string | string[],
  fieldDefinition: RecordFieldSchema,
  formRef?: any,
  recordModel: RecordModel<T>,
  recordsetFilters: any,
  className?: string;
}

const getSingularName = (pluralName: string) => {
  if (pluralName.slice(-3) === 'ies') {
    return pluralName.slice(0,-3)+'y';
  }
  if (pluralName.slice(-2) === 'es' &&
    pluralName.slice(-3) !== 'nes' &&
    pluralName.slice(-3) !== 'mes' &&
    pluralName.slice(-3) !== 'ges') {
    return pluralName.slice(0,-2);
  }
  return pluralName.slice(0,-1);
};

function RelatedFieldSelect<RecordType extends Identifiable>(props: RelatedFieldSelectProps<RecordType>): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [modelName, setModelName] = useState<string | null | undefined>();
  const [relatedModel, setRelatedModel] = useState<RecordModel<any>>();
  const [multiple, setMultiple] = useState(false);
  const { recordModel, formRef } = props;
  const leafFieldName = typeof props.fieldName === 'string' ? props.fieldName : props.fieldName[props.fieldName.length-1];

  // Check if this field is a dependsOn field for other fields
  const isDependsOnField = useMemo(() => {
    if (!recordModel.dependentFields) return false;
    return Object.values(recordModel.dependentFields).some(config => config.dependsOn === leafFieldName);
  }, [recordModel.dependentFields, leafFieldName]);

  useEffect(() => {
    const fieldModel = !!recordModel.fieldModels ? recordModel.fieldModels[leafFieldName] : undefined;
    setModelName(fieldModel);
    setLoading(true);
    let modelName = fieldModel || (
      leafFieldName
        .replace(/_([a-z])/g, (_, group1) => group1.toUpperCase())
        .replace(/^\w/, firstChar => firstChar.toUpperCase())
    )
    import('../../types/models/index').then((r: any) => {
      setLoading(false);
      let relatedModel = r[`${modelName}Model`];
      if (relatedModel === undefined && modelName.slice(-1) === 's') {   // Plural case
        relatedModel = r[`${getSingularName(modelName)}Model`];
        setMultiple(true);
      }
      setRelatedModel(relatedModel);
    });
  }, [props.fieldName]);

  // Helper function to store auxiliary data
  const storeAuxiliaryData = useMemo(() => {
    if (!isDependsOnField || !formRef) return () => {};

    return (value: string, recordList: any[]) => {
      const selectedRecord = recordList.find(record => record.id.toString() === value);

      if (selectedRecord && formRef.current) {
        const auxFieldName = `${leafFieldName}_aux`;
        formRef.current.setFieldsValue({
          [auxFieldName]: selectedRecord
        });
      }
    };
  }, [isDependsOnField, formRef, leafFieldName]);

  // Create custom onChange handler for dependsOn fields
  const handleChange = useMemo(() => {
    if (!isDependsOnField || !formRef) return undefined;

    return (value: string, recordList: any[]) => {
      storeAuxiliaryData(value, recordList);
    };
  }, [isDependsOnField, formRef, storeAuxiliaryData]);

  // Track the current field value to detect changes
  const [currentRecordList, setCurrentRecordList] = useState<any[]>([]);
  const [lastFieldValue, setLastFieldValue] = useState<string | null>(null);

  // Handle initial auxiliary data population for edit mode
  const handleInitialFetch = useMemo(() => {
    if (!isDependsOnField || !formRef) return undefined;

    return (recordList: any[]) => {
      setCurrentRecordList(recordList);

      try {
        const formValues = formRef.current?.getFieldsValue();
        const currentFieldValue = formValues?.[leafFieldName];

        if (currentFieldValue && !formValues[`${leafFieldName}_aux`]) {
          // We have a field value but no auxiliary data - this is edit mode
          storeAuxiliaryData(currentFieldValue.toString(), recordList);
          setLastFieldValue(currentFieldValue.toString());
        }
      } catch (error) {
        console.warn('Error populating initial auxiliary data:', error);
      }
    };
  }, [isDependsOnField, formRef, leafFieldName, storeAuxiliaryData]);

  // Watch for field value changes when form is reused for different records
  useEffect(() => {
    if (!isDependsOnField || !formRef || currentRecordList.length === 0) return;

    const checkFieldValueChange = () => {
      try {
        const formValues = formRef.current?.getFieldsValue();
        const currentFieldValue = formValues?.[leafFieldName];

        if (currentFieldValue && currentFieldValue.toString() !== lastFieldValue) {
          storeAuxiliaryData(currentFieldValue.toString(), currentRecordList);
          setLastFieldValue(currentFieldValue.toString());
        }
      } catch (error) {
        console.warn('Error checking field value change:', error);
      }
    };

    // Check immediately and then set up periodic checking
    checkFieldValueChange();
    const interval = setInterval(checkFieldValueChange, 1000);

    return () => clearInterval(interval);
  }, [isDependsOnField, formRef, leafFieldName, storeAuxiliaryData, currentRecordList, lastFieldValue]);

  return (
    <>
      {!!relatedModel && (
        <RecordSelect
          recordsetFilters={props.recordsetFilters}
          formName={props.fieldName}
          label={props.fieldDefinition.label}
          required={props.fieldDefinition.required}
          recordModel={relatedModel}
          mode={multiple ? "multiple" : undefined}
          allowManage
          onChange={handleChange}
          formRef={formRef}
          onInitialFetch={handleInitialFetch}
          className={props.className}
        />
      )}

      {/* Hidden form item to store auxiliary data for dependent fields */}
      {isDependsOnField && (
        <Form.Item name={`${leafFieldName}_aux`} noStyle>
          <input type="hidden" />
        </Form.Item>
      )}

      {loading && (
        <div>Loading model for "{props.fieldName}" field...</div>
      )}
      {(!loading && !relatedModel && modelName !== null) && (
        <div style={{ color: 'darkred' }}>Error importing model for "{props.fieldName}" field</div>
      )}
    </>
  )
}

export default RelatedFieldSelect;
